// Handling for the `cumul` / `cumulRefYear` / `cumulRYDValue` URL parameters
// (cumulative, time-integrated view of timeseries).
// See doc/availableURLParameters.md and doc/specs/2026-06-30-cumul-url-parameter.md.

import { isDerivParamActive } from "./derivParam"

export type CumulParams = {
  refYear: number
  rydValue: number
}

const CUMUL_ACTIVE_VALUES = ["Yes", "yes", "Y", "y"]

/**
 * Resolve the cumulative-view parameters from the current URL, or null if
 * `cumul` is unset / not one of Yes,yes,Y,y. `cumul` cannot be combined with
 * `deriv`: if both are active simultaneously, both are ignored.
 */
export function getCumulParams(): CumulParams | null {
  if (typeof window === "undefined") {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)
  const raw = urlParams.get("cumul")
  if (raw === null || !CUMUL_ACTIVE_VALUES.includes(raw)) {
    return null
  }
  if (isDerivParamActive()) {
    return null
  }
  const refYearRaw =
    urlParams.get("cumulRefYear") || urlParams.get("cumulrefyear") || "1960"
  const rydRaw =
    urlParams.get("cumulRYDValue") || urlParams.get("cumulrydvalue") || "0"
  const refYear = parseInt(refYearRaw)
  const rydValue = parseFloat(rydRaw)
  return {
    refYear: Number.isFinite(refYear) ? refYear : 1960,
    rydValue: Number.isFinite(rydValue) ? rydValue : 0,
  }
}

/**
 * Insert `|cumulative` before the last ` (` in a string (scanning from the end).
 * If no ` (` is present, append it at the end.
 */
function insertCumulativeTag(text: string): string {
  const idx = text.lastIndexOf(" (")
  if (idx === -1) {
    return text + "|cumulative"
  }
  return text.slice(0, idx) + "|cumulative" + text.slice(idx)
}

/** y-axis label transform: insert `|cumulative` before the last ` (`. */
export function applyCumulToYLabel(label: string): string {
  if (getCumulParams() === null) {
    return label
  }
  return insertCumulativeTag(label)
}

/** Chart title transform: insert `|cumulative` before the last ` (`. */
export function applyCumulToTitle(title: string): string {
  if (getCumulParams() === null) {
    return title
  }
  return insertCumulativeTag(title)
}

/**
 * Legend SubVariable transform: append `|cumul.vs.YYYY|nnnnnVarUnit`, where YYYY
 * is the reference year and nnnnnVarUnit is the reference-year anchor value
 * (reference-year value + cumulRYDValue) followed by the variable unit.
 */
export function applyCumulToSubVar(
  name: string,
  anchorValue: number | undefined,
  unit: string
): string {
  const params = getCumulParams()
  if (params === null) {
    return name
  }
  const value = anchorValue === undefined ? 0 : Math.round(anchorValue)
  return name + "|cumul.vs." + params.refYear + "|" + value + unit
}

/** Extract the trailing parenthesized unit from a variable name, e.g. "MtCO₂eq". */
export function extractUnitFromVariableName(variableName: string): string {
  const match = variableName.match(/\(([^()]*)\)\s*$/)
  return match ? match[1] : ""
}
